/* ------------------------------------------------------------------ *
 * SEO runtime layer.
 *
 *  - hreflang alternates for every supported language variant
 *  - fallback Open Graph images when a listing/partner has no image
 *  - runtime validation so invalid or missing SEO fields are never
 *    served in sitemap / Open Graph / Twitter metadata
 * ------------------------------------------------------------------ */

import { LANGUAGES, type LanguageCode } from "@/lib/i18n";
import { PUBLIC_SITE_URL } from "@/lib/share";

export type { LanguageCode };

export const SEO_LANGUAGES: LanguageCode[] = LANGUAGES.map((l) => l.code);

/** Region-qualified hreflang values (all variants target India). */
const HREFLANG: Record<LanguageCode, string> = {
  en: "en-IN",
  ta: "ta-IN",
  hi: "hi-IN",
  te: "te-IN",
};

export const DEFAULT_LANGUAGE: LanguageCode = "en";

/* ---------------- limits ---------------- */

export const SEO_LIMITS = {
  titleMin: 15,
  titleMax: 65,
  descriptionMin: 50,
  descriptionMax: 160,
} as const;

/* ---------------- fallback OG images ---------------- */

export const BRAND_LOGO_URL = `${PUBLIC_SITE_URL}/logo.png`;
export const BRAND_OG_IMAGE = `${PUBLIC_SITE_URL}/og/landoo-og.jpg`;

export const OG_FALLBACK = {
  listing: `${PUBLIC_SITE_URL}/og/listing-fallback.jpg`,
  parking: `${PUBLIC_SITE_URL}/og/parking-fallback.jpg`,
  partner: `${PUBLIC_SITE_URL}/og/partner-fallback.jpg`,
} as const;

export type OgFallbackKind = keyof typeof OG_FALLBACK;

/** True when the value is a usable absolute https image URL. */
export function isValidOgImage(value: unknown): value is string {
  if (typeof value !== "string") return false;
  const url = value.trim();
  if (!url.startsWith("https://")) return false;
  try {
    const parsed = new URL(url);
    return Boolean(parsed.hostname) && url.length <= 1000;
  } catch {
    return false;
  }
}

/**
 * Pick the best social image: the first valid absolute https candidate,
 * otherwise the branded fallback for that content kind.
 */
export function resolveOgImage(
  candidates: Array<string | null | undefined>,
  kind: OgFallbackKind,
): { url: string; isFallback: boolean } {
  for (const c of candidates) {
    if (isValidOgImage(c)) return { url: c.trim(), isFallback: false };
  }
  return { url: OG_FALLBACK[kind], isFallback: true };
}

/* ---------------- text normalisation ---------------- */

function collapse(value: unknown): string {
  return typeof value === "string" ? value.replace(/\s+/g, " ").trim() : "";
}

/** Clamp on a word boundary and add an ellipsis. */
function clamp(value: string, max: number): string {
  if (value.length <= max) return value;
  const cut = value.slice(0, max - 1);
  const space = cut.lastIndexOf(" ");
  return `${(space > max * 0.6 ? cut.slice(0, space) : cut).trimEnd()}…`;
}

export function safeSeoTitle(value: unknown, fallback: string): string {
  const text = collapse(value) || collapse(fallback) || "Landoo";
  return clamp(text, SEO_LIMITS.titleMax);
}

export function safeSeoDescription(value: unknown, fallback: string): string {
  const text = collapse(value) || collapse(fallback);
  return clamp(text || "Explore verified property listings on Landoo.", SEO_LIMITS.descriptionMax);
}

/** True when the value is an absolute http(s) URL. */
export function isValidUrl(value: unknown): value is string {
  if (typeof value !== "string" || !value.trim()) return false;
  try {
    const u = new URL(value.trim());
    return u.protocol === "https:" || u.protocol === "http:";
  } catch {
    return false;
  }
}

/** Absolute canonical URL for a site-relative path, validated. */
export function canonicalUrl(path: string): string {
  const clean = `/${String(path ?? "").replace(/^\/+/, "")}`.replace(/\s/g, "");
  return `${PUBLIC_SITE_URL}${clean === "/" ? "" : clean}`;
}

/* ---------------- hreflang ---------------- */

export interface AlternateLink {
  hreflang: string;
  href: string;
  language: LanguageCode | "x-default";
}

/** Language-variant URL: `?lang=xx` (English is the canonical, param-free URL). */
export function languageVariantUrl(url: string, code: LanguageCode): string {
  if (code === DEFAULT_LANGUAGE) return url;
  const sep = url.includes("?") ? "&" : "?";
  return `${url}${sep}lang=${code}`;
}

export function alternateLanguageLinks(url: string): AlternateLink[] {
  if (!isValidUrl(url)) return [];
  const list: AlternateLink[] = SEO_LANGUAGES.map((code) => ({
    language: code,
    hreflang: HREFLANG[code],
    href: languageVariantUrl(url, code),
  }));
  list.push({ language: "x-default", hreflang: "x-default", href: url });
  return list;
}

/* ---------------- validated head builder ---------------- */

export interface SeoIssue {
  field: string;
  level: "error" | "warning";
  message: string;
}

export interface SeoInput {
  title: string;
  description: string;
  url: string;
  image?: string | null;
  imageKind: OgFallbackKind;
  ogType?: "website" | "article" | "profile";
  twitterCard?: "summary" | "summary_large_image";
  siteName?: string;
  noindex?: boolean;
  nofollow?: boolean;
  /** Language the page is rendered in (drives og:locale). */
  language?: LanguageCode;
}

export interface SeoResult {
  title: string;
  description: string;
  url: string;
  image: string;
  imageIsFallback: boolean;
  ogType: string;
  twitterCard: string;
  language: LanguageCode;
  robots: string;
  indexable: boolean;
  alternates: AlternateLink[];
  issues: SeoIssue[];
  meta: Array<Record<string, string>>;
  links: Array<Record<string, string>>;
}

/* ---------------- indexing policy ---------------- */

export interface IndexingState {
  /** Listing approved / published, or partner profile published. */
  approved?: boolean;
  /** Partner (or listing owner) passed identity + document verification. */
  verified?: boolean;
  /** Sample / demo content that must never reach the index. */
  demo?: boolean;
}

export interface IndexingPolicy {
  noindex: boolean;
  nofollow: boolean;
  robots: string;
  reasons: string[];
}

/**
 * Single source of truth for crawler directives.
 *
 *  - not approved / not published → noindex,nofollow (nothing to rank)
 *  - demo or sample content       → noindex,nofollow
 *  - approved but unverified      → index, but nofollow outbound links
 *  - approved + verified          → index,follow with rich previews
 */
export function indexingPolicy(state: IndexingState): IndexingPolicy {
  const reasons: string[] = [];
  let noindex = false;
  let nofollow = false;

  if (state.approved === false) {
    noindex = true;
    nofollow = true;
    reasons.push("Not approved/published yet — excluded from search until approval.");
  }
  if (state.demo) {
    noindex = true;
    nofollow = true;
    reasons.push("Sample/demo content is never indexed.");
  }
  if (!noindex && state.verified === false) {
    nofollow = true;
    reasons.push("Owner is not verified — outbound links are marked nofollow.");
  }

  const robots = noindex
    ? "noindex,nofollow"
    : [
        "index",
        nofollow ? "nofollow" : "follow",
        "max-image-preview:large",
        "max-snippet:-1",
        "max-video-preview:-1",
      ].join(",");

  return { noindex, nofollow, robots, reasons };
}

/**
 * Build validated head metadata. Anything missing or invalid is repaired
 * with a safe value and reported in `issues`, so no broken tag is served.
 */
export function buildSeo(input: SeoInput): SeoResult {
  const issues: SeoIssue[] = [];

  const rawTitle = collapse(input.title);
  if (!rawTitle) issues.push({ field: "title", level: "error", message: "Title is missing." });
  else if (rawTitle.length < SEO_LIMITS.titleMin)
    issues.push({
      field: "title",
      level: "warning",
      message: `Title is short (${rawTitle.length} chars, aim for ${SEO_LIMITS.titleMin}–${SEO_LIMITS.titleMax}).`,
    });
  else if (rawTitle.length > SEO_LIMITS.titleMax)
    issues.push({
      field: "title",
      level: "warning",
      message: `Title exceeds ${SEO_LIMITS.titleMax} chars and was trimmed.`,
    });

  const rawDesc = collapse(input.description);
  if (!rawDesc)
    issues.push({ field: "description", level: "error", message: "Meta description is missing." });
  else if (rawDesc.length < SEO_LIMITS.descriptionMin)
    issues.push({
      field: "description",
      level: "warning",
      message: `Description is short (${rawDesc.length} chars, aim for ${SEO_LIMITS.descriptionMin}–${SEO_LIMITS.descriptionMax}).`,
    });
  else if (rawDesc.length > SEO_LIMITS.descriptionMax)
    issues.push({
      field: "description",
      level: "warning",
      message: `Description exceeds ${SEO_LIMITS.descriptionMax} chars and was trimmed.`,
    });

  const url = isValidUrl(input.url) ? input.url.trim() : PUBLIC_SITE_URL;
  if (!isValidUrl(input.url))
    issues.push({
      field: "canonical",
      level: "error",
      message: "Canonical URL was invalid; fell back to the site root.",
    });

  const picked = resolveOgImage([input.image], input.imageKind);
  if (picked.isFallback)
    issues.push({
      field: "og:image",
      level: "warning",
      message: "No valid image found — using the branded fallback share image.",
    });

  const title = safeSeoTitle(rawTitle, "Landoo");
  const description = safeSeoDescription(rawDesc, "Explore verified listings on Landoo.");
  const ogType = input.ogType ?? "website";
  const twitterCard = input.twitterCard ?? "summary_large_image";
  const alternates = alternateLanguageLinks(url);
  const language: LanguageCode = SEO_LANGUAGES.includes(input.language as LanguageCode)
    ? (input.language as LanguageCode)
    : DEFAULT_LANGUAGE;
  const policy = indexingPolicy({
    approved: input.noindex ? false : true,
    verified: input.nofollow ? false : undefined,
  });

  const meta: Array<Record<string, string>> = [
    { title },
    { name: "description", content: description },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:type", content: ogType },
    { property: "og:url", content: url },
    { property: "og:image", content: picked.url },
    { property: "og:image:alt", content: title },
    { property: "og:image:width", content: "1200" },
    { property: "og:image:height", content: "630" },
    { property: "og:locale", content: HREFLANG[language].replace("-", "_") },
    { property: "og:site_name", content: input.siteName ?? "Landoo" },
    { name: "twitter:card", content: twitterCard },
    { name: "twitter:title", content: title },
    { name: "twitter:description", content: description },
    { name: "twitter:image", content: picked.url },
    { name: "twitter:image:alt", content: title },
    { name: "robots", content: policy.robots },
  ];

  // Alternate locales are advertised via hreflang links: the head merger
  // dedupes meta by `property`, so repeated og:locale:alternate tags would
  // collapse into a single misleading entry.

  const links: Array<Record<string, string>> = [
    { rel: "canonical", href: url },
    ...alternates.map((a) => ({ rel: "alternate", hrefLang: a.hreflang, href: a.href })),
  ];

  return {
    title,
    description,
    url,
    image: picked.url,
    imageIsFallback: picked.isFallback,
    ogType,
    twitterCard,
    language,
    robots: policy.robots,
    indexable: !policy.noindex,
    alternates,
    issues,
    // Drop any tag whose content ended up empty — never serve an empty tag.
    meta: meta.filter((m) => Object.values(m).every((v) => typeof v === "string" && v.length > 0)),
    links: links.filter((l) => isValidUrl(l.href)),
  };
}

/* ---------------- JSON-LD structured data ---------------- */

type Json = Record<string, unknown>;

/** Drop undefined/null/empty members so no hollow node reaches the page. */
function prune<T extends Json>(node: T): T {
  const out: Json = {};
  for (const [k, v] of Object.entries(node)) {
    if (v === undefined || v === null || v === "") continue;
    if (Array.isArray(v)) {
      const arr = v.filter((i) => i !== undefined && i !== null && i !== "");
      if (arr.length) out[k] = arr;
      continue;
    }
    if (typeof v === "object") {
      const nested = prune(v as Json);
      if (Object.keys(nested).length > 1 || !("@type" in nested)) out[k] = nested;
      continue;
    }
    out[k] = v;
  }
  return out as T;
}

export const ORGANIZATION_ID = `${PUBLIC_SITE_URL}/#organization`;
export const WEBSITE_ID = `${PUBLIC_SITE_URL}/#website`;

/** Sitewide Organization + WebSite graph. */
export function organizationJsonLd(): Json {
  return {
    "@context": "https://schema.org",
    "@graph": [
      prune({
        "@type": "Organization",
        "@id": ORGANIZATION_ID,
        name: "Landoo",
        url: PUBLIC_SITE_URL,
        logo: BRAND_LOGO_URL,
        description:
          "Landoo is a GPS-first property marketplace for sale, rent, lease and parking listings across India.",
        areaServed: { "@type": "Country", name: "India" },
      }),
      prune({
        "@type": "WebSite",
        "@id": WEBSITE_ID,
        name: "Landoo",
        url: PUBLIC_SITE_URL,
        publisher: { "@id": ORGANIZATION_ID },
        inLanguage: SEO_LANGUAGES.map((c) => HREFLANG[c]),
        potentialAction: {
          "@type": "SearchAction",
          target: `${PUBLIC_SITE_URL}/search?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      }),
    ],
  };
}

export function breadcrumbJsonLd(trail: Array<{ name: string; url: string }>): Json | null {
  const items = trail.filter((t) => t.name && isValidUrl(t.url));
  if (items.length < 2) return null;
  return {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((t, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: t.name,
      item: t.url,
    })),
  };
}

export interface ListingJsonLdInput {
  seo: SeoResult;
  kind: string;
  propertyType?: string | null;
  priceLabel?: string | null;
  price?: number | null;
  areaSqft?: number | null;
  address?: string | null;
  city?: string | null;
  latitude?: number | null;
  longitude?: number | null;
  postedAt?: string | null;
  partnerName?: string | null;
  partnerUrl?: string | null;
  images?: string[];
}

/** RealEstateListing (+ Offer / Place) graph for a public listing page. */
export function listingJsonLd(input: ListingJsonLdInput): Json {
  const { seo } = input;
  const images = (input.images ?? []).filter(isValidOgImage);
  return {
    "@context": "https://schema.org",
    "@type": "RealEstateListing",
    "@id": `${seo.url}#listing`,
    name: seo.title,
    description: seo.description,
    url: seo.url,
    image: images.length ? images : [seo.image],
    datePosted: safeLastmod(input.postedAt),
    inLanguage: seo.alternates.filter((a) => a.language !== "x-default").map((a) => a.hreflang),
    isPartOf: { "@id": WEBSITE_ID },
    provider: prune({
      "@type": "RealEstateAgent",
      name: input.partnerName ?? "Landoo",
      url: input.partnerUrl ?? PUBLIC_SITE_URL,
    }),
    about: prune({
      "@type": "Accommodation",
      name: input.propertyType ?? undefined,
      floorSize: input.areaSqft
        ? { "@type": "QuantitativeValue", value: input.areaSqft, unitCode: "FTK" }
        : undefined,
      address: prune({
        "@type": "PostalAddress",
        streetAddress: input.address ?? undefined,
        addressLocality: input.city ?? undefined,
        addressCountry: "IN",
      }),
      geo:
        typeof input.latitude === "number" && typeof input.longitude === "number"
          ? { "@type": "GeoCoordinates", latitude: input.latitude, longitude: input.longitude }
          : undefined,
    }),
    offers: prune({
      "@type": "Offer",
      url: seo.url,
      priceCurrency: "INR",
      price: typeof input.price === "number" && input.price > 0 ? input.price : undefined,
      name: input.priceLabel ?? undefined,
      availability: "https://schema.org/InStock",
      category: input.kind,
    }),
  };
}

export interface PartnerJsonLdInput {
  seo: SeoResult;
  businessName: string;
  city?: string | null;
  verified?: boolean;
  averageRating?: number | null;
  totalReviews?: number | null;
  activeListings?: number | null;
}

/** RealEstateAgent / LocalBusiness graph for a public partner profile. */
export function partnerJsonLd(input: PartnerJsonLdInput): Json {
  const { seo } = input;
  const rating =
    input.totalReviews && input.totalReviews > 0
      ? {
          "@type": "AggregateRating",
          ratingValue: Number(input.averageRating ?? 0).toFixed(1),
          reviewCount: input.totalReviews,
          bestRating: 5,
          worstRating: 1,
        }
      : undefined;
  return {
    "@context": "https://schema.org",
    "@type": ["RealEstateAgent", "LocalBusiness"],
    "@id": `${seo.url}#partner`,
    name: input.businessName,
    description: seo.description,
    url: seo.url,
    image: seo.image,
    logo: seo.image,
    priceRange: "₹₹",
    inLanguage: seo.alternates.filter((a) => a.language !== "x-default").map((a) => a.hreflang),
    parentOrganization: { "@id": ORGANIZATION_ID },
    address: prune({
      "@type": "PostalAddress",
      addressLocality: input.city ?? undefined,
      addressCountry: "IN",
    }),
    areaServed: input.city ? { "@type": "City", name: input.city } : undefined,
    knowsAbout: ["Property sale", "Property rent", "Property lease", "Parking"],
    aggregateRating: rating,
    makesOffer:
      input.activeListings && input.activeListings > 0
        ? {
            "@type": "OfferCatalog",
            name: "Active listings",
            numberOfItems: input.activeListings,
          }
        : undefined,
  };
}

/** Serialise a JSON-LD node into a TanStack head `scripts` entry. */
export function jsonLdScript(node: Json | null): Array<{ type: string; children: string }> {
  if (!node) return [];
  return [{ type: "application/ld+json", children: JSON.stringify(prune(node)) }];
}

/* ---------------- admin SEO checklist ---------------- */

export type ChecklistStatus = "pass" | "warn" | "fail";

export interface ChecklistItem {
  id: string;
  label: string;
  status: ChecklistStatus;
  detail: string;
}

export interface SeoChecklist {
  items: ChecklistItem[];
  failures: number;
  warnings: number;
  /** False when a blocking field is missing/invalid — block approve/publish. */
  readyToPublish: boolean;
}

/**
 * Admin-facing pre-flight: every SEO field that must be valid before a
 * listing is approved or a partner profile is published.
 */
export function seoChecklist(seo: SeoResult, extra: ChecklistItem[] = []): SeoChecklist {
  const has = (field: string, level: "error" | "warning") =>
    seo.issues.some((i) => i.field === field && i.level === level);

  const titleLen = seo.title.length;
  const descLen = seo.description.length;

  const items: ChecklistItem[] = [
    {
      id: "title",
      label: "Meta title",
      status: has("title", "error") ? "fail" : has("title", "warning") ? "warn" : "pass",
      detail: has("title", "error")
        ? "Missing — a title is required before publishing."
        : `${titleLen}/${SEO_LIMITS.titleMax} characters.`,
    },
    {
      id: "description",
      label: "Meta description",
      status: has("description", "error")
        ? "fail"
        : has("description", "warning")
          ? "warn"
          : "pass",
      detail: has("description", "error")
        ? "Missing — add a description before publishing."
        : `${descLen}/${SEO_LIMITS.descriptionMax} characters.`,
    },
    {
      id: "canonical",
      label: "Canonical URL",
      status: has("canonical", "error") ? "fail" : "pass",
      detail: has("canonical", "error") ? "Invalid canonical URL." : seo.url,
    },
    {
      id: "og-image",
      label: "Social share image",
      status: seo.imageIsFallback ? "warn" : "pass",
      detail: seo.imageIsFallback
        ? "No listing image — the branded fallback will be shared."
        : "Using a real image (1200×630 preview).",
    },
    {
      id: "og-tags",
      label: "Open Graph tags",
      status: ["og:title", "og:description", "og:url", "og:image", "og:type"].every((p) =>
        seo.meta.some((m) => m.property === p && m.content),
      )
        ? "pass"
        : "fail",
      detail: `og:type ${seo.ogType} · ${seo.meta.filter((m) => m.property?.startsWith("og:")).length} tags`,
    },
    {
      id: "twitter-tags",
      label: "Twitter card",
      status: ["twitter:card", "twitter:title", "twitter:description", "twitter:image"].every((n) =>
        seo.meta.some((m) => m.name === n && m.content),
      )
        ? "pass"
        : "fail",
      detail: seo.twitterCard,
    },
    {
      id: "hreflang",
      label: "Language alternates",
      status: seo.alternates.length >= SEO_LANGUAGES.length + 1 ? "pass" : "warn",
      detail: `${seo.alternates.length} hreflang links (incl. x-default).`,
    },
    {
      id: "robots",
      label: "Crawler directive",
      status: seo.indexable ? "pass" : "warn",
      detail: seo.robots,
    },
    ...extra,
  ];

  const failures = items.filter((i) => i.status === "fail").length;
  const warnings = items.filter((i) => i.status === "warn").length;
  return { items, failures, warnings, readyToPublish: failures === 0 };
}

/* ---------------- sitemap guards ---------------- */

export function escapeXml(value: string): string {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");
}

/** Validate a sitemap `loc`; returns null when it must not be served. */
export function safeSitemapLoc(path: string): string | null {
  if (typeof path !== "string" || !path.startsWith("/")) return null;
  if (/\s/.test(path)) return null;
  const url = `${PUBLIC_SITE_URL}${path === "/" ? "/" : path.replace(/\/+$/, "")}`;
  return isValidUrl(url) ? escapeXml(url) : null;
}

/** Validate a lastmod value; returns undefined when it is unusable. */
export function safeLastmod(value: unknown): string | undefined {
  if (typeof value !== "string" || !value) return undefined;
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return undefined;
  if (d.getTime() > Date.now() + 86_400_000) return undefined; // no future dates
  return d.toISOString();
}
